import { emit } from '../../events';
import { noteUserGesture } from '../activeAdapter';
import {
  highlighted,
  reduceScan,
  restingScan,
  startScan,
  type ScanEvent,
  type ScanState,
} from '../scanMachine';
import { readScanTargets, scanRows, type ScanRow } from '../scanOrder';
import type { InputAdapter, InputCapabilities, Intent } from '../InputAdapter';

/**
 * Single-switch row/column scanning (3.3).
 *
 * Plumbing only: the order is in `scanOrder.ts` and the behaviour is in `scanMachine.ts`, both pure and both
 * tested without a browser. What this file owns is the interval, the two ways a switch can arrive, and painting
 * the highlight.
 *
 * ── The whole screen is one switch ──
 *
 * A tap anywhere is a switch press. That works without a single surface change because of stage 1's C62 fix:
 * `ActivateButton` ignores a pointer-derived click while the adapter is timed, so tapping a key does not
 * activate that key — it advances or selects whatever the highlight is on. Which is the correct behaviour for
 * somebody who cannot reliably aim, and it would have been a confusing hybrid without that fix.
 *
 * Space and Enter do the same thing, because a Bluetooth switch presents as a keyboard (§2.5). **That path is
 * built and unverified** — there is no switch to test with, and the device checklist says so rather than
 * implying coverage.
 */

const CAPABILITIES: InputCapabilities = {
  needsFocusRing: true,
  /** A scan tick is not a Safari user gesture. Same consequence as dwell, and the reason 3.2 exists. */
  isTimed: true,
  needsAudioCue: true,
};

export interface ScanningSettings {
  scanIntervalMs: number;
  scanLoops: number;
}

/** Attribute the stylesheet watches. One per highlighted target; a whole row at row level. */
const HIGHLIGHT = 'scanHighlight';

export function createScanningAdapter(getSettings: () => ScanningSettings): InputAdapter {
  let root: HTMLElement | null = null;
  let emitIntent: ((intent: Intent) => void) | null = null;

  let state: ScanState = restingScan();
  let rows: ScanRow[] = [];
  let timer: ReturnType<typeof setInterval> | null = null;
  let painted: HTMLElement[] = [];
  /** Steps since the cycle began, for the diagnostics figure 3.3 asks for. */
  let steps = 0;

  /**
   * Re-read the targets from the DOM.
   *
   * Called at the start of a cycle, after a selection, and when the surface changes — **never continuously**.
   * Prediction candidates appear and vanish while the person types, and a list that changed under the highlight
   * would move it out from beneath a finger already committing to a press.
   */
  const resnapshot = (): void => {
    rows = root ? scanRows(readScanTargets(root)) : [];
  };

  const clearPaint = (): void => {
    for (const element of painted) delete element.dataset[HIGHLIGHT];
    painted = [];
  };

  const paint = (): void => {
    clearPaint();
    if (!root) return;

    for (const id of highlighted(state, rows)) {
      const element = root.querySelector<HTMLElement>(`[data-activate-id="${id}"]`);
      if (!element) continue;
      element.dataset[HIGHLIGHT] = 'true';
      painted.push(element);
    }
  };

  const step = (event: ScanEvent): void => {
    const settings = getSettings();
    const result = reduceScan(state, event, rows, { loops: settings.scanLoops }, steps);
    const previous = state;
    state = result.state;
    steps += 1;

    if (result.effect) {
      // Reported before activating, because activating may replace the surface — and then the count would be
      // attributed to whatever came next.
      emit({ type: 'ScanSelection', steps: result.effect.steps });
      steps = 0;

      clearPaint();
      emitIntent?.({ kind: 'activate', targetId: result.effect.targetId });

      // The surface may have changed under us. Read it again before painting anything.
      resnapshot();
      paint();
      return;
    }

    // Coming out of rest, or starting a fresh pass: the targets may be stale.
    if (previous.kind === 'rest' && state.kind !== 'rest') {
      steps = 0;
      resnapshot();
    }
    if (state.kind === 'rest') {
      clearPaint();
      return;
    }

    paint();
  };

  const onSwitch = (): void => {
    // The press itself is a real gesture even though the tick that positioned the highlight was not. Noted so
    // that anything keyed off a gesture — audio unlock in particular — still works.
    noteUserGesture();
    step({ kind: 'press' });
  };

  const onPointerDown = (event: PointerEvent): void => {
    // Any contact anywhere. `preventDefault` is deliberately not called: the synthetic click still fires, and
    // `ActivateButton` discards it because this adapter is timed (C62).
    if (event.isPrimary) onSwitch();
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    // How a Bluetooth switch arrives. Built, and unverified — there is no switch to test with.
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    onSwitch();
  };

  return {
    id: 'scanning',
    capabilities: CAPABILITIES,

    attach(element: HTMLElement, emitFn: (intent: Intent) => void): void {
      root = element;
      emitIntent = emitFn;

      resnapshot();
      state = startScan();
      steps = 0;
      paint();

      // A plain interval rather than rAF: this ticks once or twice a second, and a frame loop would be running
      // sixty times as often to do nothing. Read live from settings on every tick, which is what makes
      // `scanIntervalMs` adjustable while the person is using it.
      let currentInterval = getSettings().scanIntervalMs;
      const start = (): void => {
        timer = setInterval(() => {
          const wanted = getSettings().scanIntervalMs;
          if (wanted !== currentInterval) {
            // The caregiver moved the slider. Restart at the new rate rather than waiting out the old one.
            currentInterval = wanted;
            if (timer !== null) clearInterval(timer);
            start();
          }
          step({ kind: 'tick' });
        }, currentInterval);
      };
      start();

      root.addEventListener('pointerdown', onPointerDown);
      document.addEventListener('keydown', onKeyDown);
    },

    detach(): void {
      if (timer !== null) clearInterval(timer);
      timer = null;
      root?.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      clearPaint();
      state = restingScan();
      rows = [];
      steps = 0;
      root = null;
      emitIntent = null;
    },
  };
}
